import React, { useEffect, useMemo, useState } from 'react';
import { usePublishPageContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishPageContext';
import { Publishable, PublishableType } from '~/types/models/publishable';
import { MAX_TESTIMONIALS } from '~/App/Modules/Testimonials/Pages/Publish/constants';
import WebFont from 'webfontloader';
import { useOrderedTestimonials } from '~/App/Modules/Testimonials/Pages/Publish/Editor/hooks';
import {
  computeWallOfLoveColumnTiles,
  PublishableEmbed,
  PublishableEmbedProps,
} from '@vimily/bonjoro-ui-components-v2/PublishableEmbed';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { LoadingPage } from './LoadingPage';

export interface PublishProps extends PublishableEmbedProps {
  publish?: Publishable;
}

export function Publish({
  publish: propPublish,
  disableResponsiveness = false,
  disableScroll = false,
  containerClassName,
  containerStyle,
  ...props
}: PublishProps) {
  const context = usePublishPageContext()!;
  const [isResizerReady, setIsResizerReady] = useState(false);

  const publish = propPublish ?? context.publish;

  const isSlider = publish.type === PublishableType.Slider;
  const isWoL = publish.type === PublishableType.WallOfLove;

  const settings = useMemo(() => {
    const defaults = {
      enableMaxHeight: isSlider,
      allowMobileTileRestriction: isWoL,
      maxHeight: 450,
      tilesPerPage: 3,
      autoSlide: false,
      allowDynamicTiles: isWoL,
      hideName: false,
      hideTitle: false,
      hideProfileImage: false,
      hideDate: false,
      hideCarouselIndicator: false,
      hideCarouselChevron: false,
    };

    return {
      enableMaxHeight:
        props?.enableMaxHeight ??
        publish.metadata?.enable_max_height ??
        defaults.enableMaxHeight,
      maxHeight:
        props?.maxHeight ?? publish.metadata?.max_height ?? defaults?.maxHeight,
      allowMobileTileRestriction:
        props?.allowMobileTileRestriction ??
        publish.metadata?.allow_mobile_tile_restriction ??
        defaults?.allowMobileTileRestriction,
      allowDynamicTiles:
        props?.allowDynamicTiles ??
        publish.metadata?.allow_dynamic_tiles ??
        defaults?.allowDynamicTiles,
      tilesPerPage:
        props?.tilesPerPage ??
        publish.metadata?.tiles_per_page ??
        defaults?.tilesPerPage,
      autoSlide:
        props?.autoSlide ?? publish.metadata?.auto_slide ?? defaults?.autoSlide,
      hideName:
        props?.hideName ?? publish.metadata?.hide_name ?? defaults?.hideName,
      hideTitle:
        props?.hideTitle ?? publish.metadata?.hide_title ?? defaults?.hideTitle,
      hideProfileImage:
        props?.hideProfileImage ??
        publish.metadata?.hide_profile_image ??
        defaults?.hideProfileImage,
      hideDate:
        props?.hideDate ?? publish.metadata?.hide_date ?? defaults?.hideDate,
      hideCarouselIndicator:
        props?.hideCarouselIndicator ??
        publish.metadata?.hide_carousel_indicator ??
        defaults?.hideCarouselIndicator,
      hideCarouselChevron:
        props?.hideCarouselChevron ??
        publish.metadata?.hide_carousel_chevron ??
        defaults?.hideCarouselChevron,

      primaryColor:
        props?.primaryColor ?? publish.metadata?.style?.primary_color,
      cornerRadius:
        props?.cornerRadius ?? publish.metadata?.style?.corner_radius,
      dropShadow: props?.dropShadow ?? publish.metadata?.style?.drop_shadow,
      border: props?.border ?? publish.metadata?.style?.border,
      borderSize: props?.borderSize ?? publish.metadata?.style?.border_size,
      borderColor: props?.borderColor ?? publish.metadata?.style?.border_color,
      fontFamily: props?.fontFamily ?? publish.metadata?.style?.font_family,

      hideBonjoroBadge: props.hideBonjoroBadge ?? false,
      isResizerReady,
      containerClassName,
      containerStyle,
      disableResponsiveness,
      disableScroll,
    };
  }, [props, publish]);

  const onIframeResizer = useGlobalEvent(
    'iframeResizer::ready' as keyof WindowEventMap
  );

  onIframeResizer(() => {
    setIsResizerReady(true);
    computeWallOfLoveColumnTiles();
  });

  const payload = useMemo(() => {
    const payload = {
      publish,
      data: props?.data ?? publish?.publishable_resources ?? [],
      type: props?.type ?? (publish.type as PublishableType),
    };

    if (payload.type === PublishableType.Slider) {
      return {
        ...payload,
        data: payload.data.slice(0, MAX_TESTIMONIALS),
      };
    }

    return payload;
  }, [
    props?.data,
    props?.type,
    publish?.publishable_resources,
    publish.type,
    publish?.metadata?.display_order,
    publish?.metadata?.order,
    publish?.tag,
  ]);

  const orderedTestimonialsOptions = useMemo(
    () => ({
      data: payload.data,
      displayOrder: publish?.metadata?.display_order,
      order: publish?.metadata?.order,
    }),
    [payload.data, publish?.metadata?.display_order, publish?.metadata?.order]
  );

  const sortedData = useOrderedTestimonials(
    publish,
    orderedTestimonialsOptions
  );

  const publishProps = {
    ...settings,
    ...payload,
    data: sortedData,
  };

  const isLoadedInsideIFrame = useMemo(() => window.self !== window.top, []);

  useEffect(() => {
    if (!settings.fontFamily) return;

    WebFont.load({
      google: {
        families: [settings.fontFamily],
      },
    });
  }, [settings.fontFamily]);

  return (
    <>
      {isLoadedInsideIFrame && !isResizerReady && <LoadingPage />}
      <PublishableEmbed {...publishProps} />
    </>
  );
}
